// Third-party
import React from 'react';
import classNames from 'classnames';

// Components
import { RiskProfileCard } from '../common';
import { UnitInput } from '../inputs';

// Util
import { FormattedMessage, formatMessage } from '../../../utils/i18n';
import { SecondaryLink } from '../buttons';
import RiskProfileItem from '@/types/risk-profile-item';
import Locale from '@/types/locale';
import ContactRiskProfile from '@/types/contact-risk-profile';
import User from '@/types/user';
import Nullable from '@/types/nullable';

type Props = {
  riskProfiles: RiskProfileItem[];
  locale: Locale;
  user?: Nullable<User>;
  newRiskProfile: Partial<ContactRiskProfile>;
  recommendedRiskProfile?: RiskProfileItem;
  setRiskProfileScore: (score?: number) => void;
  setRiskProfile: () => void;
  selectRiskProfile: (profile: RiskProfileItem) => void;
  minRiskCalculation?: boolean;
  scoreCalculation?: boolean;
  scrollTo?: (id?: string) => void;
  onBack?: () => void;
};

type State = {
  inputScore?: number;
};

class RiskProfiles extends React.Component<Props> {
  readonly state: State = {
    inputScore: this.props.scoreCalculation ? this.props.newRiskProfile.score : undefined,
  };

  componentDidMount(): void {
    if (this.props.newRiskProfile.profile && this.props.minRiskCalculation && this.props.scrollTo) {
      const id = `risk-profile-card-${this.props.newRiskProfile.profile._id}`;
      this.props.scrollTo(id);
    }
  }

  onScoreChange = (score: string) => {
    score = score.trim();
    const value = score === '' ? undefined : Number(score);

    this.props.setRiskProfileScore(value);
    this.props.setRiskProfile();
    this.setState({ inputScore: value });
  };

  onSelectProfileCard = (profile: RiskProfileItem) => {
    this.props.selectRiskProfile(profile);
  };

  renderProfileCards = (showScoreInput?: boolean) => {
    const { riskProfiles, locale, user, newRiskProfile, minRiskCalculation } = this.props;
    const score = this.state.inputScore;
    const currentProfile = newRiskProfile.profile;
    const hideScoreRange = minRiskCalculation;

    let profiles = riskProfiles;
    if (showScoreInput && score !== undefined) {
      profiles = riskProfiles.filter(profile => profile.scoreRange && profile.scoreRange.min <= score && score <= profile.scoreRange.max);
    }

    const { recommendedRiskProfile } = this.props;
    return profiles.map(profile => {
      const recommended = recommendedRiskProfile && recommendedRiskProfile._id === profile._id;
      const classes = classNames({
        active: currentProfile && currentProfile._id === profile._id,
      });

      return (
        <RiskProfileCard
          className={classes}
          key={profile._id}
          locale={locale}
          user={user}
          riskProfile={profile}
          onSelect={() => this.onSelectProfileCard(profile)}
          hideScoreRange={hideScoreRange}
          recommended={recommended}
        />
      );
    });
  };

  render() {
    const { scoreCalculation, minRiskCalculation, onBack, riskProfiles } = this.props;
    const { inputScore } = this.state;

    const hasProfileWithScore = riskProfiles.some(profile => (profile.sectionScoreRanges && profile.sectionScoreRanges.length > 0) || profile.scoreRange);
    const showScoreInput = hasProfileWithScore && !minRiskCalculation && scoreCalculation;

    return (
      <div className="risk-profiles">
        <div className="risk-profiles-header d-flex flex-column">
          {onBack && (
            <div>
              <SecondaryLink className="back-btn" onClick={onBack} icon="arrow-left">
                <FormattedMessage id="CreateRiskProfile.BackToQuestionnaire" />
              </SecondaryLink>
            </div>
          )}

          <div className="input-row d-flex align-items-center">
            <span className="flex-grow-1 header-title" data-private data-dd-privacy="mask">
              <FormattedMessage id="CreateRiskProfile.RiskProfile" />
            </span>

            {showScoreInput && (
              <UnitInput
                className="score-input"
                type="number"
                value={inputScore}
                leftSection={<i className="icon-score" />}
                onValueChange={this.onScoreChange}
                placeholder={formatMessage('CreateRiskProfile.CustomScore')}
              />
            )}
          </div>
        </div>

        {this.renderProfileCards(showScoreInput)}
      </div>
    );
  }
}

export default RiskProfiles;
